import { getMarketSubpath, getBasePathname } from "../helpers/market";

/**
 * Parses rich-text content and
 *  * replaces shortcodes with HTML
 *  * replaces same-origin absolute URLs with locale-aware relative ones
 *
 * Available shortcodes:
 * [book-strip index="1" style="buy|display"]:
 * References a product strip metafield using "index" and replaces the shortcode with strip markup.
 * The markup for each strip is already rendered on the page based on the product variants
 * defined on a metafield on the article record. See `sections/main-article.liquid`.
 */
export default class ArticleContent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.anchorNodes.forEach((node) => this.maybeRelativizeAnchor(node));

    this.html = this.innerHTML;

    this.insertProductStrips();
  }

  get anchorNodes() {
    return this.querySelectorAll("[href]");
  }

  get currentUrl() {
    return new URL(window.location);
  }

  get productStripRegex() {
    return /\[book-strip(\s[\s\S]*?)?\]/g;
  }

  get indexRegex() {
    // Shopify's RTE may inject hidden <span> tags if a shortcode has been copy/pasted
    return /index=["'](?:<\/?span>)*(?<indexString>\d+)(?:<\/?span>)*["']/i;
  }

  get styleRegex() {
    return /style=["'](?<style>[\w\s]*)["']/i;
  }

  get defaultStyle() {
    return "buy";
  }

  get altStyle() {
    return "display";
  }

  get productStripClassName() {
    return "c-product-strip";
  }

  get productStripIdPrefix() {
    return "product-strip";
  }

  get productStripInstances() {
    return this.innerHTML.match(this.productStripRegex) || [];
  }

  get hasProductStrips() {
    return !!this.productStripInstances.length;
  }

  // replace any same-origin absolute URLs with locale-aware relative ones
  maybeRelativizeAnchor(node) {
    const href = node.getAttribute("href");

    try {
      const anchorUrl = new URL(href);

      if (anchorUrl.origin !== this.currentUrl.origin) return;

      // strip any existing market subpath
      const basePathname = getBasePathname(anchorUrl.pathname);

      // prepend the subpath for the customer's market
      const marketSubpath = getMarketSubpath(this.dataset.market);
      const marketPathname = `${marketSubpath}${basePathname}`;

      node.setAttribute("href", marketPathname);
    } catch (error) {
      // relative URLs will error, which we just ignore
    }
  }

  insertProductStrips = () => {
    if (!this.hasProductStrips) return;

    this.productStripInstances.forEach((instance) =>
      this.insertProductStrip(instance),
    );
    // Replace HTML only once
    this.innerHTML = this.html;
    // and remove the server-rendered strips from the DOM
    const variantsEl = document.getElementById("product-strip-variants");
    variantsEl.remove();
  };

  insertProductStrip(instance) {
    // look up `index` param (default to "1")
    const indexMatch = instance.match(this.indexRegex);
    const index = indexMatch?.groups.indexString || "1";

    // match index to rendered book strip ID
    const productStripEl = document.getElementById(
      `${this.productStripIdPrefix}-${index}`,
    );
    if (!productStripEl) return;

    // set style className based on `style` param
    const styleMatch = instance.match(this.styleRegex);
    // be strict about using default style unless alt style is clearly indicated
    const style =
      styleMatch?.groups?.style === this.altStyle
        ? this.altStyle
        : this.defaultStyle;
    productStripEl.classList.add(
      `${this.productStripClassName}--style-${style}`,
    );

    // replace shortcode with product strip HTML
    const instanceRegex = this.stringToRegExp(instance);
    this.html = this.html.replace(instanceRegex, productStripEl.outerHTML);
  }

  // https://gist.github.com/BrandonZacharie/1879052
  stringToRegExp(value, flags = "i") {
    return new RegExp(
      value.replace(/[[\]\\{}()+*?.$^|]/g, function (match) {
        return "\\" + match;
      }),
      flags,
    );
  }
}
